import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { StateContext } from "../globalContext/context";
import { iGlobalContext } from "../globalContext/interface";
import { useHistoryContext } from "../historyContext/historyContext";
import { iHistoryContext } from "../historyContext/interface";
import { useSocketContext } from "../socketContext/SocketContext";
import { usePocContext } from "../pocContext/pocContext";
import { ROUTES } from "../../constant";
import { APP_CONFIG } from "../../constant/common";
import { API_KEY } from "../../constant/environment";
import { ApiConfig } from "../../constant/api_constants/url";
import { API_METHODS, ScreenTypes } from "../../enums";
import { fetchApi } from "../../utils/apiFunction";
import { encryptData } from "../../utils/api/encryption";
import { resolveBoolFlag } from "../../utils/appConfig/resolveBoolFlag";
import { getChatId } from "../../utils/chat/chatId";
import { isSseTransportEnabled } from "../../utils/sse/sseTransport";
import { getUserTimezoneHeader } from "../../utils/userTimezone";
import { voiceBus } from "../../services/voice/voiceBus";
import { Exchange } from "../../services/voice/liveTranscript";
import { TranscriptCleaner } from "../../services/voice/transcriptCleaner";
import { TranscriptBackup, TranscriptBatch } from "../../services/voice/transcriptBackup";
import { VoiceStatus, iVoiceModeContext, Turn, VoiceSession } from "./types";
import type { VoiceRuntime, VoiceHandlers } from "./runtime";
import { createVoiceAnalytics } from "./voiceAnalytics";
import { readStatus, createTranscriptIO } from "./transcriptIO";
import { createBubbles } from "./bubbles";
import { createHeardAnswers } from "./heardAnswers";
import { createExchanges } from "./exchanges";
import { createHandOffs } from "./handOffs";
import { createNarration } from "./narration";
import { createLiveSession } from "./liveSession";

export type { VoiceStatus, iVoiceModeContext } from "./types";

const CHAT_PATHS = [ROUTES.Home, ROUTES.TopDestination, ROUTES.ItineraryDetails, ROUTES.NewTrip];

const VoiceModeContext = createContext<iVoiceModeContext>({
  voiceEnabled: false,
  voiceStatus: "off",
  isVoiceActive: false,
  voiceRequestOpen: false,
  muted: false,
  sendChip: () => undefined,
  startVoice: () => undefined,
  stopVoice: () => undefined,
  noteUserStop: () => undefined,
  toggleMute: () => undefined,
});

export const VoiceModeProvider = ({ children }: { children: ReactNode }) => {
  const state: iGlobalContext = useContext(StateContext);
  const poc = usePocContext();
  const { handleOpenSocketConnection } = useSocketContext();
  const history: iHistoryContext = useHistoryContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { appConfig, userInfo, accessJWT, chatData, firstScreen, screen, isMobile } = state;
  const voiceEnabled =
    !!userInfo?.loggedIn &&
    resolveBoolFlag(appConfig, APP_CONFIG.VOICE_MODE_ENABLED) &&
    isSseTransportEnabled(appConfig);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>("off");
  const isVoiceActive = voiceStatus !== "off";
  const [voiceRequestOpen, setVoiceRequestOpen] = useState(false);
  const [muted, setMuted] = useState(false);

  const live = useRef({ state, poc, handleOpenSocketConnection, history, location, navigate });
  live.current = { state, poc, handleOpenSocketConnection, history, location, navigate };

  const sessionRef = useRef<VoiceSession | null>(null);
  const turnRef = useRef<Turn | null>(null);
  const chatIdRef = useRef("");
  const deferredRef = useRef<
    Array<{ user: string; reply: string; liveSessionId: string | null; shownId: string | null; session: VoiceSession; exchange: Exchange }>
  >([]);
  const lastFlushRef = useRef(0);
  const recoveredRef = useRef(false);
  const repliesRef = useRef<Array<{ turn: Turn; cleaner: TranscriptCleaner }>>([]);
  const emptiedRef = useRef<VoiceSession | null>(null);
  const openedFromRef = useRef<{ session: VoiceSession | null; firstScreen: string; screen: string; path: string } | null>(null);
  const mutedRef = useRef(false);

  const sendBatch = useCallback(async (batch: TranscriptBatch, keepalive: boolean): Promise<boolean> => {
    const jwt = live.current.state.accessJWT;
    if (!jwt) return false;
    if (keepalive) {
      const response = await fetch(ApiConfig.getFullUrl("xenia/voice/transcript"), {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "text/plain",
          "x-access-token": jwt,
          "x-api-key": API_KEY || "",
          ...getUserTimezoneHeader(),
        },
        body: encryptData(batch),
      });
      return response.ok && readStatus(await response.text());
    }
    const result = await fetchApi(jwt, "xenia/voice/transcript", API_METHODS.POST, batch);
    return result?.statusCode === 200;
  }, []);

  const backupRef = useRef<TranscriptBackup | null>(null);
  if (!backupRef.current) backupRef.current = new TranscriptBackup(sendBatch);

  const handlers = useRef({} as VoiceHandlers);
  const rt: VoiceRuntime = {
    live,
    sessionRef,
    turnRef,
    chatIdRef,
    deferredRef,
    lastFlushRef,
    recoveredRef,
    repliesRef,
    emptiedRef,
    openedFromRef,
    mutedRef,
    backupRef,
    handlers,
    voiceEnabled,
    setVoiceStatus,
    setVoiceRequestOpen,
    setMuted,
  };
  const transcriptIO = createTranscriptIO(rt);
  const voiceAnalytics = createVoiceAnalytics(rt);
  const bubbles = createBubbles(rt, transcriptIO);
  const heardAnswers = createHeardAnswers(rt, transcriptIO, bubbles);
  const exchanges = createExchanges(rt, transcriptIO, bubbles, heardAnswers);
  const handOffs = createHandOffs(rt, transcriptIO, bubbles, exchanges);
  const narration = createNarration(rt, transcriptIO, voiceAnalytics, bubbles);
  const { onLiveEvent, onLost, tick, startVoice, endVoice, toggleMute, onCleaned } = createLiveSession(
    rt,
    transcriptIO,
    voiceAnalytics,
    bubbles,
    heardAnswers,
    exchanges,
    handOffs,
    narration,
  );
  const { requestOpened, requestFailed, requestClosed, requestMasked, voiceFrame, typedTurn, sendChip, noteUserStop, checkoutNote } = narration;
  const { flushDeferred, keepEmptied } = exchanges;

  handlers.current = { onLiveEvent, onLost, requestOpened, requestFailed, requestClosed, requestMasked, voiceFrame, typedTurn, sendChip, checkoutNote, tick, flushDeferred, startVoice, endVoice, noteUserStop, toggleMute, onCleaned, keepEmptied };

  useEffect(
    () =>
      voiceBus.attach({
        requestOpened: (request) => handlers.current.requestOpened(request),
        requestFailed: (request) => handlers.current.requestFailed(request),
        requestClosed: (request) => handlers.current.requestClosed(request),
        requestMasked: (request) => handlers.current.requestMasked(request),
        voiceFrame: (frame, replyKind) => handlers.current.voiceFrame(frame, replyKind),
        typedTurn: (turn) => handlers.current.typedTurn(turn),
        checkoutNote: (text) => handlers.current.checkoutNote(text),
      }),
    [],
  );

  useEffect(() => {
    if (!isVoiceActive) return;
    const timer = setInterval(() => handlers.current.tick(), 100);
    return () => clearInterval(timer);
  }, [isVoiceActive]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void backupRef.current?.flush(true);
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, []);

  useEffect(() => {
    if (!voiceEnabled || !accessJWT || recoveredRef.current) return;
    recoveredRef.current = true;
    void backupRef.current?.recover(voiceBus.getLiveSessionId());
  }, [voiceEnabled, accessJWT]);

  useEffect(() => {
    if (!isVoiceActive) return;
    if (!voiceEnabled) {
      handlers.current.endVoice("disabled");
      return;
    }
    const onChat =
      CHAT_PATHS.includes(location.pathname) &&
      (isMobile ||
        (screen !== ScreenTypes.newBooking && [ScreenTypes.NewTrip, ScreenTypes.topDestination, ""].includes(firstScreen)));
    const chatId = chatData.length > 0 ? getChatId() : "";
    if (handlers.current.keepEmptied(onChat && chatData.length === 0)) return;
    if (!onChat || chatId !== chatIdRef.current) handlers.current.endVoice("left-chat");
  }, [isVoiceActive, voiceEnabled, isMobile, location.pathname, screen, firstScreen, chatData]);

  useEffect(() => () => handlers.current.endVoice("unmount", true), []);

  const startVoiceStable = useCallback(() => handlers.current.startVoice(), []);
  const stopVoiceStable = useCallback(() => handlers.current.endVoice("mic-tap"), []);
  const noteUserStopStable = useCallback(() => handlers.current.noteUserStop(), []);
  const toggleMuteStable = useCallback(() => handlers.current.toggleMute(), []);
  const sendChipStable = useCallback((text: string) => handlers.current.sendChip(text), []);

  const value = useMemo(
    () => ({
      voiceEnabled,
      voiceStatus,
      isVoiceActive,
      voiceRequestOpen,
      muted,
      sendChip: sendChipStable,
      startVoice: startVoiceStable,
      stopVoice: stopVoiceStable,
      noteUserStop: noteUserStopStable,
      toggleMute: toggleMuteStable,
    }),
    [voiceEnabled, voiceStatus, isVoiceActive, voiceRequestOpen, muted, sendChipStable, startVoiceStable, stopVoiceStable, noteUserStopStable, toggleMuteStable],
  );

  return <VoiceModeContext.Provider value={value}>{children}</VoiceModeContext.Provider>;
};

export const useVoiceMode = () => useContext(VoiceModeContext);
